// Admin → Delegation policy (Delegation ↔ Leave workflow).
// Tenant-configurable: who must delegate before leave, when the delegate
// confirms, CC rules, eligible delegates, date buffers. Stored on the auth
// side (tenant.settings.delegation, versioned + audited); every delegation and
// leave request records the version that governed it. Mirrors the
// ScopePolicyPage / HRActionSettings patterns — no new settings framework.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Info } from 'lucide-react';
import { RequirePermission } from '@/rbac/RequirePermission';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import { getDelegationPolicy, saveDelegationPolicy, getRoles, type DelegationPolicy } from '@/services/rbacService';

const ORG_LEVELS = ['employee', 'manager', 'director', 'executive', 'ceo'];

const DelegationPolicyPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const k = (key: string, dflt: string, opts: Record<string, unknown> = {}) => t(`delegation_policy.${key}`, { defaultValue: dflt, ...opts });
  const [policy, setPolicy] = useState<DelegationPolicy | null>(null);
  const [roles, setRoles] = useState<{ key: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDelegationPolicy().then(setPolicy).catch((e) => toast.error(getApiErrorMessage(e, 'Could not load the delegation policy')));
    getRoles()
      .then((r) => setRoles([...(r.systemRoles ?? []), ...(r.customRoles ?? [])].map((x: any) => ({ key: String(x.key), name: String(x.name ?? x.key) }))))
      .catch(() => setRoles([]));
  }, []);

  const set = <K extends keyof DelegationPolicy>(key: K, value: DelegationPolicy[K]) => setPolicy((p) => (p ? { ...p, [key]: value } : p));
  const toggleSubject = (kind: 'role' | 'org_level', key: string) => {
    if (!policy) return;
    const has = policy.requireForLeave.subjects.some((s) => s.kind === kind && s.key === key);
    set('requireForLeave', {
      ...policy.requireForLeave,
      subjects: has ? policy.requireForLeave.subjects.filter((s) => !(s.kind === kind && s.key === key)) : [...policy.requireForLeave.subjects, { kind, key }],
    });
  };
  const toggleEligibleRole = (key: string) => {
    if (!policy) return;
    const has = policy.eligibleDelegates.roles.includes(key);
    set('eligibleDelegates', { ...policy.eligibleDelegates, roles: has ? policy.eligibleDelegates.roles.filter((r) => r !== key) : [...policy.eligibleDelegates.roles, key] });
  };

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const saved = await saveDelegationPolicy(policy);
      setPolicy(saved);
      toast.success(k('saved', 'Delegation policy saved'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save the delegation policy'));
    } finally {
      setSaving(false);
    }
  };

  const selectCls = 'mt-1 w-full rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal';
  const numCls = 'mt-1 w-28 rounded-[10px] border border-border bg-transparent px-3 py-2 text-charcoal';
  const tenantRoles = roles.filter((r) => !/^platform_|^auditor$|^compliance_officer$|^audit_administrator$|^tax_officer$/.test(r.key));

  return (
    <RequirePermission perm="user:role:assign">
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-h2 text-charcoal">{k('title', 'Delegation policy')}</h1>
            <p className="text-sm text-muted-foreground">{k('subtitle', 'Who must delegate before taking leave, when the delegate confirms, who is copied, and who may receive a delegation.')}</p>
            {policy && <Badge variant="outline" className="mt-2">{k('version', 'Version {{v}}', { v: policy.version })}</Badge>}
          </div>
        </div>

        {!policy ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            {/* A. Require delegation for leave */}
            <section className="rounded-[14px] border border-border p-5 space-y-3">
              <h2 className="text-charcoal font-medium">{k('require_title', 'Require delegation for leave')}</h2>
              <select className={selectCls} value={policy.requireForLeave.mode} onChange={(e) => set('requireForLeave', { ...policy.requireForLeave, mode: e.target.value as DelegationPolicy['requireForLeave']['mode'] })}>
                <option value="disabled">{k('require_disabled', 'Disabled')}</option>
                <option value="managers">{k('require_managers', 'Managers only')}</option>
                <option value="everyone">{k('require_everyone', 'Everyone')}</option>
                <option value="subjects">{k('require_subjects', 'Selected roles / org levels')}</option>
              </select>
              {policy.requireForLeave.mode === 'subjects' && (
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">{k('subjects_roles', 'Roles')}</div>
                    <div className="flex flex-wrap gap-2">
                      {tenantRoles.map((r) => {
                        const on = policy.requireForLeave.subjects.some((s) => s.kind === 'role' && s.key === r.key);
                        return (
                          <button key={r.key} type="button" onClick={() => toggleSubject('role', r.key)} className={`rounded-full border px-3 py-1 text-xs ${on ? 'border-primary-500 bg-primary-500/10 text-charcoal' : 'border-border text-muted-foreground'}`}>
                            {r.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">{k('subjects_org_levels', 'Org levels')}</div>
                    <div className="flex flex-wrap gap-2">
                      {ORG_LEVELS.map((lvl) => {
                        const on = policy.requireForLeave.subjects.some((s) => s.kind === 'org_level' && s.key === lvl);
                        return (
                          <button key={lvl} type="button" onClick={() => toggleSubject('org_level', lvl)} className={`rounded-full border px-3 py-1 text-xs capitalize ${on ? 'border-primary-500 bg-primary-500/10 text-charcoal' : 'border-border text-muted-foreground'}`}>
                            {lvl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* B. Confirmation timing */}
            <section className="rounded-[14px] border border-border p-5 space-y-3">
              <h2 className="text-charcoal font-medium">{k('confirmation_title_v2', 'Must the delegate accept?')}</h2>
              <select className={selectCls} value={policy.confirmation} onChange={(e) => set('confirmation', e.target.value as DelegationPolicy['confirmation'])}>
                <option value="not_required">{k('confirmation_not_required_v2', 'No — cover is confirmed as soon as it is set up')}</option>
                <option value="before_submission">{k('confirmation_before_submission_v2', 'Yes — ask them when the leave is requested')}</option>
                <option value="before_final_approval">{k('confirmation_before_final_approval_v2', 'Yes — track it for the approver')}</option>
              </select>
              {/* Owner decision 2026-10-22: acceptance is shown, never a gate. */}
              <p className="text-xs text-muted-foreground flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{k('confirmation_never_blocks', "Acceptance never stops someone submitting leave or a manager approving it. Until the delegate accepts, the request shows cover as pending — and as needing attention if the time off starts soon or the delegate declines.")}</p>
              <p className="text-xs text-muted-foreground flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{k('approval_note', 'Delegations of sensitive capabilities still need Company Admin approval as before.')}</p>
            </section>

            {/* C. CC policy */}
            <section className="rounded-[14px] border border-border p-5 space-y-3">
              <h2 className="text-charcoal font-medium">{k('cc_title', 'CC policy')}</h2>
              <select className={selectCls} value={policy.cc.mode} onChange={(e) => set('cc', { ...policy.cc, mode: e.target.value as 'optional' | 'required' })}>
                <option value="optional">{k('cc_optional', 'Optional')}</option>
                <option value="required">{k('cc_required', 'Required')}</option>
              </select>
              {([
                ['autoManager', k('cc_auto_manager', "Automatically copy the delegator's direct manager")],
                ['autoHr', k('cc_auto_hr', 'Automatically copy HR')],
                ['allowManual', k('cc_allow_manual', 'Allow additional manual CC recipients')],
              ] as const).map(([field, label]) => (
                <label key={field} className="flex items-center justify-between gap-3 text-sm text-charcoal">
                  <span>{label}</span>
                  <Switch checked={policy.cc[field]} onCheckedChange={(v) => set('cc', { ...policy.cc, [field]: v })} />
                </label>
              ))}
              <p className="text-xs text-muted-foreground">{k('cc_note', 'CC recipients get visibility and notifications only — never delegated authority.')}</p>
            </section>

            {/* D. Eligible delegates */}
            <section className="rounded-[14px] border border-border p-5 space-y-3">
              <h2 className="text-charcoal font-medium">{k('eligible_title', 'Who can receive a delegation')}</h2>
              <select className={selectCls} value={policy.eligibleDelegates.rule} onChange={(e) => set('eligibleDelegates', { ...policy.eligibleDelegates, rule: e.target.value as DelegationPolicy['eligibleDelegates']['rule'] })}>
                <option value="any">{k('eligible_any', 'Any eligible employee')}</option>
                <option value="same_department">{k('eligible_same_department', 'Same department')}</option>
                <option value="same_or_higher_org_level">{k('eligible_same_or_higher_org_level', 'Same or higher org level')}</option>
              </select>
              <div className="text-sm">
                <div className="text-muted-foreground mb-1">{k('eligible_roles', 'Limit to roles (optional)')}</div>
                <div className="flex flex-wrap gap-2">
                  {tenantRoles.map((r) => {
                    const on = policy.eligibleDelegates.roles.includes(r.key);
                    return (
                      <button key={r.key} type="button" onClick={() => toggleEligibleRole(r.key)} className={`rounded-full border px-3 py-1 text-xs ${on ? 'border-primary-500 bg-primary-500/10 text-charcoal' : 'border-border text-muted-foreground'}`}>
                        {r.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* F. Date buffers */}
            <section className="rounded-[14px] border border-border p-5 space-y-3">
              <h2 className="text-charcoal font-medium">{k('buffers_title', 'Date policy when delegation starts from leave')}</h2>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <label className="text-muted-foreground">
                  {k('buffers_start', 'Start delegation N days before leave')}
                  <input type="number" min={0} max={30} className={numCls} value={policy.dateBuffers.startDaysBefore} onChange={(e) => set('dateBuffers', { ...policy.dateBuffers, startDaysBefore: Number(e.target.value) })} />
                </label>
                <label className="text-muted-foreground">
                  {k('buffers_end', 'End delegation N days after leave')}
                  <input type="number" min={0} max={30} className={numCls} value={policy.dateBuffers.endDaysAfter} onChange={(e) => set('dateBuffers', { ...policy.dateBuffers, endDaysAfter: Number(e.target.value) })} />
                </label>
              </div>
            </section>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving}>{saving ? '…' : k('save', 'Save policy')}</Button>
            </div>
          </>
        )}
      </div>
    </RequirePermission>
  );
};

export default DelegationPolicyPage;
